<script setup lang="ts">
import { ref, watch, onUnmounted } from "vue";
import { storeToRefs } from "pinia";
import { useBacksideTemplateStore } from "@/stores/backsideTemplate";
import { formatSwedishPostalCode } from "@/utils";
import {
  BACKSIDE_FORMAT,
  BACKSIDE_IMAGE_PATH,
  BACKSIDE_TEMPLATE,
} from "@/constant";
import templateHtml from "@/templates/backside-a6.html?raw";

const store = useBacksideTemplateStore();
const { previewPdfUrl, isGeneratingPreview, error, successMessage } =
  storeToRefs(store);

// Edit src/templates/backside-a6.html to change the A6 backside layout.
const sampleData = store.sampleData[BACKSIDE_FORMAT.A6];
const formErrors = ref<Record<string, string>>({});

const validateAndGenerateHtml = () => {
  const errors: Record<string, string> = {};
  if (!sampleData.receiver.name.trim()) errors["receiver.name"] = "Required";
  if (!sampleData.receiver.address.trim())
    errors["receiver.address"] = "Required";
  if (!sampleData.receiver.postalCode.trim())
    errors["receiver.postalCode"] = "Required";
  if (!sampleData.receiver.city.trim()) errors["receiver.city"] = "Required";
  if (!sampleData.date.trim()) errors["date"] = "Required";
  if (!sampleData.trackingCode.trim()) errors["trackingCode"] = "Required";
  if (!sampleData.orderId.trim()) errors["orderId"] = "Required";
  formErrors.value = errors;
  if (Object.keys(errors).length > 0) return;
  generateHtmlPreview();
};

const htmlPreviewContent = ref<string | null>(null);
const previewWrapper = ref<HTMLElement | null>(null);
const previewScale = ref(1);
const PREVIEW_W = 578;
const PREVIEW_H = 416;
let resizeObserver: ResizeObserver | null = null;
watch(previewWrapper, (el: HTMLElement | null) => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  if (el) {
    resizeObserver = new ResizeObserver((entries) => {
      previewScale.value = entries[0].contentRect.width / PREVIEW_W;
    });
    resizeObserver.observe(el);
  }
});
// Stamp filename for HTML preview (e.g. "se-summer.png"). Files must be in public/stamps/.
// A6 uses the square stamp variant per Figma; landscape would overflow into the postmark.
const stampPreviewPath = ref("/stamps/se_spring_square.png");

const resolvePreviewStampPath = (path: string) => {
  const trimmedPath = path.trim();
  if (!trimmedPath) {
    return "/stamps/se_summer_square.png";
  }
  return trimmedPath;
};

const buildRenderedHtml = (): string => {
  let html = templateHtml;
  const conditionalVars: Record<string, boolean> = {
    showCutMarks: sampleData.showCutMarks,
    prioritaire: sampleData.prioritaire,
    sweden: sampleData.sweden,
    branding: !!(sampleData.branding && sampleData.branding.trim()),
  };
  html = html.replace(
    /\{\{#if (\w+) \}\}([\s\S]*?)\{\{\/if\}\}/g,
    (_, varName, content) => (conditionalVars[varName] ? content : ""),
  );
  html = html.split("{{ receiver.name }}").join(sampleData.receiver.name);
  html = html
    .split("{{{ receiver.name2 }}}")
    .join(sampleData.receiver.name2 ? "<br>" + sampleData.receiver.name2 : "");
  html = html.split("{{ receiver.address }}").join(sampleData.receiver.address);
  html = html
    .split("{{ receiver.postalCode }}")
    .join(
      formatSwedishPostalCode(
        sampleData.receiver.postalCode,
        sampleData.receiver.country,
      ),
    );
  html = html.split("{{ receiver.city }}").join(sampleData.receiver.city);
  html = html.split("{{ receiver.country }}").join(sampleData.receiver.country);
  html = html.split("{{ date }}").join(sampleData.date);
  html = html.split("{{ trackingCode }}").join(sampleData.trackingCode);
  html = html.split("{{ orderId }}").join(sampleData.orderId);
  html = html.split("{{ attribution }}").join(sampleData.attribution);
  html = html
    .split("{{ backImagePath }}")
    .join("/" + BACKSIDE_IMAGE_PATH[BACKSIDE_FORMAT.A6]);
  html = html
    .split("{{ stamp }}")
    .join(resolvePreviewStampPath(stampPreviewPath.value));
  html = html.split("{{ templateFolderPath }}").join("");
  html = html.split("{{ branding }}").join(sampleData.branding ?? "");
  html = html.split('src="stamps/').join('src="/stamps/');
  html = html.split('src="promo/').join('src="/promo/');
  html = html.split('src="cutmark.png"').join('src="/cutmark.png"');
  html = html
    .split('url("Posten_4state.ttf")')
    .join('url("/Posten_4state.ttf")');
  html = html
    .split('url("PostNordSans-Regular.ttf")')
    .join('url("/PostNordSans-Regular.ttf")');
  html = html
    .split('url("PostNordSans-Medium.ttf")')
    .join('url("/PostNordSans-Medium.ttf")');
  return html;
};

const generateHtmlPreview = () => {
  let html = buildRenderedHtml();
  // Preview-only: make body a positioning context so absolutely-positioned elements
  // (stamp, prioritaire, postmark) anchor correctly, matching Puppeteer behaviour.
  html = html.replace(
    "</head>",
    "<style>" +
      "html { height: 100%; overflow: hidden; }" +
      "body { position: relative; overflow: hidden; }" +
      ".stamp { position: absolute !important; top: var(--stamp-margin); right: var(--stamp-margin); transform: scale(0.32); transform-origin: top right; width: 600px; max-width: 600px; }" +
      ".prioritaire { max-width: 600px; }" +
      "</style></head>",
  );
  htmlPreviewContent.value = html;
};

const generatePreview = () => {
  store.generatePreview(
    BACKSIDE_FORMAT.A6,
    templateHtml,
    stampPreviewPath.value,
  );
};

const showConfirmDialog = ref(false);
const confirmTemplate = () => {
  showConfirmDialog.value = false;
  store.successMessage = BACKSIDE_TEMPLATE.CONFIRM_SUCCESS;
};

onUnmounted(() => {
  store.clearMessages();
  store.clearPreview();
  htmlPreviewContent.value = null;
  resizeObserver?.disconnect();
});

const toggleShowCutMarks = () => {
  sampleData.showCutMarks = !sampleData.showCutMarks;
};

const togglePrioritaire = () => {
  sampleData.prioritaire = !sampleData.prioritaire;
};

const toggleSwedenPromo = () => {
  sampleData.sweden = !sampleData.sweden;
};
</script>

<template>
  <section style="padding: 24px 0">
    <pn-toast
      v-if="successMessage"
      type="success"
      :message="successMessage"
      style="position: fixed; top: 80px; right: 24px; z-index: 1000"
    />
    <pn-toast
      v-if="error"
      type="error"
      :message="error"
      style="position: fixed; top: 80px; right: 24px; z-index: 1000"
    />

    <h2 class="pn-l" style="margin-bottom: 16px">A6 Template</h2>
    <p class="pn-xs" style="margin-bottom: 24px; color: #666">
      Edit the <code>templateHtml</code> constant in
      <code>BacksideTemplateA6.vue</code> to modify the A6 layout. Changes are
      deployed via the pipeline.
    </p>

    <section aria-label="Sample data for preview" style="margin-bottom: 24px">
      <h3 class="pn-m" style="margin-bottom: 12px">Sample Data</h3>
      <div
        style="
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          max-width: 700px;
        "
      >
        <pn-input
          label="Receiver name"
          v-model="sampleData.receiver.name"
          :invalid="!!formErrors['receiver.name']"
          :error="formErrors['receiver.name']"
        />
        <pn-input
          label="Receiver name2 (optional)"
          v-model="sampleData.receiver.name2"
        />
        <pn-input
          label="Address"
          v-model="sampleData.receiver.address"
          :invalid="!!formErrors['receiver.address']"
          :error="formErrors['receiver.address']"
        />
        <pn-input
          label="Postal code"
          v-model="sampleData.receiver.postalCode"
          :invalid="!!formErrors['receiver.postalCode']"
          :error="formErrors['receiver.postalCode']"
        />
        <pn-input
          label="City"
          v-model="sampleData.receiver.city"
          :invalid="!!formErrors['receiver.city']"
          :error="formErrors['receiver.city']"
        />
        <pn-input label="Country" v-model="sampleData.receiver.country" />
        <pn-input
          label="Date"
          v-model="sampleData.date"
          :invalid="!!formErrors['date']"
          :error="formErrors['date']"
        />
        <pn-input
          label="Tracking code"
          v-model="sampleData.trackingCode"
          :invalid="!!formErrors['trackingCode']"
          :error="formErrors['trackingCode']"
        />
        <pn-input
          label="Order ID"
          v-model="sampleData.orderId"
          :invalid="!!formErrors['orderId']"
          :error="formErrors['orderId']"
        />
        <pn-input label="Attribution" v-model="sampleData.attribution" />
        <pn-input
          label="Stamp image (preview only)"
          v-model="stampPreviewPath"
        />
        <pn-input
          label="Branding image path (optional)"
          v-model="sampleData.branding"
        />
      </div>
      <div style="display: flex; gap: 24px; margin-top: 12px">
        <pn-checkbox
          label="Show cut marks"
          :checked="sampleData.showCutMarks"
          @change="toggleShowCutMarks"
        />
        <pn-checkbox
          label="Prioritaire"
          :checked="sampleData.prioritaire"
          @change="togglePrioritaire"
        />
        <pn-checkbox
          label="Sweden promo"
          :checked="sampleData.sweden"
          @change="toggleSwedenPromo"
        />
      </div>
    </section>

    <div
      style="
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 24px;
        flex-wrap: wrap;
      "
    >
      <pn-button variant="secondary" @click="validateAndGenerateHtml">
        Generate HTML
      </pn-button>
      <pn-button @click="generatePreview" :disabled="isGeneratingPreview">
        Generate Preview PDF
      </pn-button>
      <pn-spinner v-if="isGeneratingPreview" />
      <a
        v-if="previewPdfUrl"
        :href="previewPdfUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="pn-xs"
      >
        Open in New Tab
      </a>
    </div>

    <div v-if="htmlPreviewContent" style="margin-bottom: 24px">
      <h3 class="pn-m" style="margin-bottom: 8px">HTML Preview</h3>
      <p class="pn-2xs" style="margin-bottom: 8px; color: #888">
        Layout rendered in browser with sample data. Fonts and images load only
        via the backend PDF.
      </p>
      <div
        ref="previewWrapper"
        :style="{
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          height: `${Math.round(PREVIEW_H * previewScale)}px`,
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          background: 'white',
        }"
      >
        <iframe
          :srcdoc="htmlPreviewContent"
          :style="{
            width: `${PREVIEW_W}px`,
            height: `${PREVIEW_H}px`,
            border: 'none',
            display: 'block',
            transform: `scale(${previewScale})`,
            transformOrigin: 'top left',
          }"
          title="A6 template HTML preview"
          sandbox="allow-same-origin allow-scripts"
        />
      </div>
    </div>

    <div v-if="previewPdfUrl" style="margin-bottom: 24px">
      <h3 class="pn-m" style="margin-bottom: 12px">PDF Preview</h3>
      <object
        :data="previewPdfUrl"
        type="application/pdf"
        style="
          width: 100%;
          height: 600px;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
        "
        aria-label="A6 backside template PDF preview"
      >
        <p class="pn-xs">
          PDF preview is not supported in this browser.
          <a :href="previewPdfUrl" target="_blank" rel="noopener noreferrer"
            >Open PDF directly.</a
          >
        </p>
      </object>
    </div>

    <pn-button
      v-if="previewPdfUrl"
      variant="secondary"
      @click="showConfirmDialog = true"
    >
      Confirm Template
    </pn-button>

    <div
      v-if="showConfirmDialog"
      style="
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
      "
      @click.self="showConfirmDialog = false"
    >
      <div
        style="
          background: white;
          border-radius: 8px;
          padding: 32px;
          max-width: 480px;
          width: 100%;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
        "
      >
        <h3 class="pn-m" style="margin-bottom: 12px">
          {{ BACKSIDE_TEMPLATE.CONFIRM_TITLE }}
        </h3>
        <p class="pn-s" style="margin-bottom: 24px">
          {{ BACKSIDE_TEMPLATE.CONFIRM_MESSAGE }}
        </p>
        <div style="display: flex; gap: 12px; justify-content: flex-end">
          <pn-button variant="secondary" @click="showConfirmDialog = false">
            Cancel
          </pn-button>
          <pn-button @click="confirmTemplate">Confirm</pn-button>
        </div>
      </div>
    </div>
  </section>
</template>
